import { DAY_KEYS, DAY_LABELS, type DayKey, isDayKey } from "@/lib/days";
import type { WorkoutLogConfig, WorkoutPlanningConfig } from "@/lib/schema";
import type { WidgetSettingsEditorProps } from "@/widgets/types";

type Props = WidgetSettingsEditorProps<WorkoutLogConfig>;

const DEFAULT_PLANNING: WorkoutPlanningConfig = {
  points: 2,
  deadline: "mon",
  weekGoal: 2,
};

const NO_DEADLINE = "none";

function numberOrNaN(value: string): number {
  return value === "" ? Number.NaN : Number(value);
}

export function SettingsEditor({
  config,
  onChange,
  errors = {},
  scoringField,
}: Props) {
  const fieldError = (key: string) =>
    errors[key] ? (
      <p className="field-error" data-testid={`widget-error-${key}`}>
        {errors[key]}
      </p>
    ) : null;

  const { planning } = config;
  const workoutsGoal = planning
    ? (config.workoutsWeekGoal ?? config.weekGoal)
    : config.weekGoal;

  const goalWorkouts =
    Number.isFinite(workoutsGoal) && config.points >= 1
      ? Math.ceil(workoutsGoal / config.points)
      : null;

  // Switching planning on splits the one goal into two Scorable parts
  // (docs/adr/0010): the existing goal becomes Økter's, and the instance's
  // goal becomes the sum. Switching it off folds them back.
  const togglePlanning = (on: boolean) => {
    if (on) {
      onChange({
        planning: DEFAULT_PLANNING,
        workoutsWeekGoal: config.weekGoal,
        weekGoal: config.weekGoal + DEFAULT_PLANNING.weekGoal,
      });
    } else {
      onChange({
        planning: undefined,
        workoutsWeekGoal: undefined,
        weekGoal: workoutsGoal,
      });
    }
  };

  const planningPatch = (patch: Partial<WorkoutPlanningConfig>) => {
    if (!planning) return;
    const next = { ...planning, ...patch };
    onChange({
      planning: next,
      weekGoal: workoutsGoal + next.weekGoal,
    });
  };

  return (
    <div
      className="widget-settings"
      data-testid={`widget-settings-${config.id}`}
    >
      <div className="setting-row">
        <label className="setting-label" htmlFor={`label-${config.id}`}>
          Tittel
        </label>
        <div className="setting-control">
          <input
            id={`label-${config.id}`}
            className="input"
            type="text"
            value={config.label}
            onChange={(e) => onChange({ label: e.target.value })}
            data-testid="widget-label-input"
          />
          {fieldError("label")}
        </div>
      </div>

      {planning ? (
        <div className="setting-row">
          <label
            className="setting-label"
            htmlFor={`workoutsgoal-${config.id}`}
          >
            Ukesmål · økter
            <small>Følges som egen linje i ukesoppsummeringen.</small>
          </label>
          <div className="setting-control">
            <div className="num-input">
              <input
                id={`workoutsgoal-${config.id}`}
                type="number"
                min={0}
                step={0.25}
                value={Number.isFinite(workoutsGoal) ? workoutsGoal : ""}
                onChange={(e) => {
                  const next = numberOrNaN(e.target.value);
                  onChange({
                    workoutsWeekGoal: next,
                    weekGoal: next + planning.weekGoal,
                  });
                }}
                data-testid="workout-log-workouts-weekgoal-input"
              />
              <span className="unit">p</span>
            </div>
            {fieldError("workoutsWeekGoal")}
          </div>
        </div>
      ) : (
        scoringField
      )}

      <div className="setting-row">
        <label className="setting-label" htmlFor={`points-${config.id}`}>
          Poeng
          <small>Poeng per loggført økt.</small>
        </label>
        <div className="setting-control">
          <div className="num-input">
            <input
              id={`points-${config.id}`}
              type="number"
              min={1}
              value={config.points}
              onChange={(e) =>
                onChange({ points: Math.max(0, Number(e.target.value) || 0) })
              }
              data-testid="widget-points-input"
            />
            <span className="unit">p</span>
          </div>
          {fieldError("points")}
        </div>
      </div>

      {goalWorkouts !== null ? (
        <p className="hint" data-testid="workout-goal-hint">
          Ukesmål {workoutsGoal}p = {goalWorkouts}{" "}
          {goalWorkouts === 1 ? "økt" : "økter"} denne uka.
        </p>
      ) : null}

      <div className="setting-row">
        <span className="setting-label">
          Planlegging
          <small>Planlegg uka fra kortet: økter på dager, hvile ellers.</small>
        </span>
        <div className="setting-control">
          <label className="check-option">
            <input
              type="checkbox"
              checked={planning !== undefined}
              onChange={(e) => togglePlanning(e.target.checked)}
              data-testid="workout-log-planning-toggle"
            />
            Planlegg uka
          </label>
        </div>
      </div>

      {planning ? (
        <>
          <div className="setting-row">
            <label
              className="setting-label"
              htmlFor={`planningpoints-${config.id}`}
            >
              Poeng for planlegging
              <small>Gis den dagen planen lagres første gang.</small>
            </label>
            <div className="setting-control">
              <div className="num-input">
                <input
                  id={`planningpoints-${config.id}`}
                  type="number"
                  min={1}
                  value={
                    Number.isFinite(planning.points) ? planning.points : ""
                  }
                  onChange={(e) =>
                    planningPatch({ points: numberOrNaN(e.target.value) })
                  }
                  data-testid="workout-log-planning-points-input"
                />
                <span className="unit">p</span>
              </div>
              {fieldError("planningPoints")}
            </div>
          </div>

          <div className="setting-row">
            <label
              className="setting-label"
              htmlFor={`planningdeadline-${config.id}`}
            >
              Planleggingsfrist
              <small>
                Siste dag planen gir poeng. En plan lagret senere gjelder, men
                gir ikke poeng.
              </small>
            </label>
            <div className="setting-control">
              <select
                id={`planningdeadline-${config.id}`}
                className="input"
                value={planning.deadline ?? NO_DEADLINE}
                onChange={(e) => {
                  const value = e.target.value;
                  planningPatch({
                    deadline: isDayKey(value) ? (value as DayKey) : null,
                  });
                }}
                data-testid="workout-log-planning-deadline-select"
              >
                {DAY_KEYS.map((k) => (
                  <option key={k} value={k}>
                    {DAY_LABELS[k]}
                  </option>
                ))}
                <option value={NO_DEADLINE}>Ingen frist</option>
              </select>
            </div>
          </div>

          <div className="setting-row">
            <label
              className="setting-label"
              htmlFor={`planninggoal-${config.id}`}
            >
              Ukesmål · planlegging
              <small>Følges som egen linje i ukesoppsummeringen.</small>
            </label>
            <div className="setting-control">
              <div className="num-input">
                <input
                  id={`planninggoal-${config.id}`}
                  type="number"
                  min={0}
                  step={0.25}
                  value={
                    Number.isFinite(planning.weekGoal) ? planning.weekGoal : ""
                  }
                  onChange={(e) =>
                    planningPatch({ weekGoal: numberOrNaN(e.target.value) })
                  }
                  data-testid="workout-log-planning-weekgoal-input"
                />
                <span className="unit">p</span>
              </div>
              {fieldError("planningWeekGoal")}
            </div>
          </div>

          <p
            className="hint"
            data-testid={`workout-log-total-goal-${config.id}`}
          >
            {Number.isFinite(config.weekGoal)
              ? `Samlet ukesmål: ${config.weekGoal}p`
              : "Samlet ukesmål: fyll ut begge delmålene."}
          </p>
        </>
      ) : null}
    </div>
  );
}
